import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dailyToSeries, needsAttention, attentionSummary } from '../domainMetrics';
import { FALLBACK_TAG, money } from '../earnings';
import { AttentionBadges } from './AttentionCard';
import Sparkline, { SERIES_COLORS } from '../ui/Sparkline';
import { sortDomains } from './sortDomains';
import { formatCtr } from '../offerMetrics';
import { formatDuration, READ_MIN_SAMPLE } from '../pageMetrics';
import { visibleColumns, tableMinWidth, siteColumnWidth, moneySpans } from './siteColumns';

const HOSTING_STATE = { 'new-fastapi-websites': 'success', 'default (old)': 'warn' };

/*
 * The fleet table.  `domains` is the already-filtered list; this component
 * owns the sort state.  `metrics` supplies per-domain accessors:
 *   hits, hitsDaily, earnings, impressions, imprDaily, adsPerHit, ecpm, rpm,
 *   amzImpr, amzClicks (Amazon offer impressions/clicks, our Snowplow counts),
 *   readRate, readTime, readers, readViews (page pings; readTotals is the
 *   pooled all-sites summary, since medians cannot be summed)
 * (all over the fixed 30-day window), and `earnings` the selected period's
 * money: adsense / amazon / total accessors (null = that source cannot cover
 * the period), plus portfolio totals and the Amazon money no site owns.
 */
export default function SitesTable({
  domains, metrics, earnings, trendDays, view, isMobile, onFavoriteToggle, onSelect,
}) {
  const [sortColumn, setSortColumn] = useState('is_favorite');
  const [sortDirection, setSortDirection] = useState('desc');
  const navigate = useNavigate();

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const indicator = (column) => {
    if (sortColumn !== column) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const Th = ({ column, width, num, title, children }) => (
    <th
      className={[column ? 'sortable' : '', num ? 'num' : ''].join(' ').trim() || undefined}
      style={{ width }}
      title={title}
      onClick={column ? () => handleSort(column) : undefined}
    >
      {children}{column ? indicator(column) : ''}
    </th>
  );

  const rows = sortDomains(domains, sortColumn, sortDirection, metrics, earnings);
  const periodLabel = earnings.period ? earnings.period.label.replace(' (Amazon report)', '') : '';
  /* which columns the selected preset shows at this width (siteColumns.js) */
  const show = visibleColumns(view, { wide: !isMobile });

  const open = (domain, path = '') => {
    onSelect && onSelect(domain);
    navigate(`/domain/${domain.id}${path}`);
  };

  const sum = (fn) => rows.reduce((acc, d) => acc + (fn(d) || 0), 0);
  const totalHits = sum(metrics.hits);
  const humanKnown = !!metrics.humanHits && rows.some((d) => metrics.humanHits(d) != null);
  const totalHuman = humanKnown ? sum(metrics.humanHits) : null;
  const totalImpr = sum(metrics.impressions);
  /* Some sites earn through AdSense but have no Snowplow ad tracking at all.
     Their revenue has no impressions to divide by, so charging it against
     other sites' impressions inflated eCPM ($2.85 against a true $0.68). */
  const tracked = rows.filter((d) => metrics.impressions(d) > 0);
  const trackedEarn = tracked.reduce((acc, d) => acc + metrics.earnings(d), 0);
  const trackedImpr = tracked.reduce((acc, d) => acc + metrics.impressions(d), 0);
  const viewed = rows.filter((d) => metrics.hits(d) > 0);
  const viewedEarn = viewed.reduce((acc, d) => acc + metrics.earnings(d), 0);
  const viewedHits = viewed.reduce((acc, d) => acc + metrics.hits(d), 0);

  return (
    <div className="table-wrap">
      <table
        className="data-table"
        style={{ minWidth: isMobile ? 0 : `${tableMinWidth(show)}px`, tableLayout: isMobile ? 'auto' : 'fixed' }}
      >
        <thead>
          <tr>
            <Th column="is_favorite" width="44px" title="Favourites first">★</Th>
            {/* fixed layout: an unsized column collapses to zero, so the site column is always sized */}
            <Th column="hostname" width={`${siteColumnWidth(show)}px`}>Site</Th>
            {show.dns && <Th column="dns_target" width="180px">DNS target</Th>}
            {show.hosting && <Th column="hosting_service" width="180px">Hosting</Th>}
            {show.hits && <Th column="page_hits" width="100px" num
                title="Every page view in the last 30 complete MST days, known bots (by user agent) excluded">Hits (30d)</Th>}
            {show.human && <Th column="human_hits" width="100px" num
                title={metrics.humanRule || 'Page views that are probably human'}>Human</Th>}
            {show.readRate && <Th column="read_rate" width="80px" num
                title="Share of page views (homepage excluded) where the reader was active for at least 30 seconds, last 30 complete MST days">Read rate</Th>}
            {show.readTime && <Th column="read_time" width="80px" num
                title={`Median active time of the readers who stayed 30s+: half of them stayed at least this long. Muted when fewer than ${READ_MIN_SAMPLE} readers.`}>Read time</Th>}
            {show.adsense && <Th column="adsense" width="100px" num
                title={`AdSense estimated earnings, ${periodLabel}. ${earnings.adsensePeriod.available ? '' : earnings.adsensePeriod.note}`}>AdSense</Th>}
            {show.amazon && <Th column="amazon" width="100px" num
                title={`Amazon Associates earnings attributed to this site by its tracking ID, ${periodLabel}. ${earnings.amazonPeriod.available ? '' : earnings.amazonPeriod.note}`}>Amazon</Th>}
            {show.total && <Th column="total_earnings" width="110px" num
                title={`AdSense + Amazon, ${periodLabel}`}>Total</Th>}
            {show.impr && <Th column="ad_impressions" width="100px" num>Ad impr</Th>}
            {show.adsPerHit && <Th column="ads_per_hit" width="86px" num
                title="Ad impressions divided by page views — the average number of ads served per visit">Ads/hit</Th>}
            {show.ecpm && <Th column="ecpm" width="86px" num
                title='eCPM (AdSense "Impression RPM"): estimated earnings per 1,000 ad impressions'>eCPM</Th>}
            {show.rpm && <Th column="rpm" width="86px" num
                title="Page RPM: AdSense estimated earnings per 1,000 page views (30d). Equals eCPM × ads/hit">RPM</Th>}
            {show.amzImpr && <Th column="amz_impr" width="90px" num
                title="Amazon offers seen at least half in view, last 30 complete MST days. Our Snowplow count, not Amazon's.">Amz impr</Th>}
            {show.amzClicks && <Th column="amz_clicks" width="90px" num
                title="Clicks on Amazon offer links, last 30 complete MST days, with CTR (clicks / offer impressions). Our Snowplow count; Amazon's own clicks come from the Associates report.">Amz clicks</Th>}
            {show.trend && (
              <th style={{ width: '140px' }}>
                Trend (30d)
                <div style={{ fontWeight: 400, fontSize: '10px', marginTop: '2px', textTransform: 'none' }}>
                  <span style={{ color: SERIES_COLORS.hits }}>&#9473; hits</span>
                  {' '}
                  <span style={{ color: SERIES_COLORS.impressions }}>&#9473; impr</span>
                </div>
              </th>
            )}
            {show.pages && <Th column="page_count" width="76px" num>Pages</Th>}
            {show.newMo && <Th column="pages_created_this_month" width="86px" num>New (mo)</Th>}
            {show.scheduled && <Th column="pages_scheduled" width="96px" num>Scheduled</Th>}
            {show.attention && (
              <Th column="attention" width="210px"
                  title="Drafts, pages missing a description, and main-image errors (from the last page-stats refresh)">
                Attention
              </Th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((domain) => {
            const hits = metrics.hits(domain);
            const human = metrics.humanHits ? metrics.humanHits(domain) : null;
            const adsense = earnings.adsense(domain);
            const amazon = earnings.amazon(domain);
            const total = earnings.total(domain);
            const hasTag = !!String(domain.amazon_tag || '').trim();
            const impr = metrics.impressions(domain);
            const amzImpr = metrics.amzImpr(domain);
            const amzClicks = metrics.amzClicks(domain);
            const adsPerHit = metrics.adsPerHit(domain);
            const ecpm = metrics.ecpm(domain);
            const rpm = metrics.rpm(domain);
            const readRate = metrics.readRate(domain);
            const readTime = metrics.readTime(domain);
            const readers = metrics.readers(domain);
            const trendSeries = [
              { key: 'hits', color: SERIES_COLORS.hits, data: dailyToSeries(metrics.hitsDaily(domain), trendDays) },
              { key: 'impressions', color: SERIES_COLORS.impressions, data: dailyToSeries(metrics.imprDaily(domain), trendDays) },
            ];
            return (
              <tr key={domain.id} className="clickable" onClick={() => open(domain)}>
                <td style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={(e) => { e.stopPropagation(); onFavoriteToggle(domain.id, domain.is_favorite); }}
                    aria-pressed={!!domain.is_favorite}
                    title={domain.is_favorite ? 'Remove from my favorites' : 'Add to my favorites'}
                    style={{ padding: '0 4px', fontSize: '16px', lineHeight: 1 }}
                  >
                    {domain.is_favorite ? '⭐' : '☆'}
                  </button>
                </td>
                <td style={{ overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <span
                      aria-hidden="true"
                      style={{
                        width: '22px', height: '22px', flexShrink: 0, borderRadius: '4px',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: domain.primary_color || '#f3f4f6',
                      }}
                    >
                      {domain.favicon && (
                        <img src={domain.favicon} alt="" width="16" height="16"
                             style={{ backgroundColor: '#fff', borderRadius: '2px', padding: '1px' }} />
                      )}
                    </span>
                    <span title={domain.hostname}
                          style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {domain.hostname}
                    </span>
                    <a
                      href={`/domain/${domain.id}/pages`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(domain, '/pages'); }}
                      title={`All pages for ${domain.hostname}`}
                      className="small"
                      style={{ flexShrink: 0, textDecoration: 'none', whiteSpace: 'nowrap' }}
                    >
                      Pages →
                    </a>
                  </div>
                </td>
                {show.dns && (
                  <td className="muted small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={domain.dns_target || ''}>
                    {domain.dns_target || '—'}
                  </td>
                )}
                {show.hosting && (
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={domain.hosting_service || ''}>
                    {domain.hosting_service ? (
                      <span className={`badge badge-${HOSTING_STATE[domain.hosting_service] || 'archived'}`}
                            style={{ textTransform: 'none' }}>
                        {domain.hosting_service}
                      </span>
                    ) : <span className="muted">—</span>}
                  </td>
                )}
                {show.hits && <td className="num">{hits > 0 ? hits.toLocaleString() : <span className="muted">—</span>}</td>}
                {show.human && (
                  <td className="num" title={human != null && hits > 0
                    ? `${human.toLocaleString()} of ${hits.toLocaleString()} views probably human; ${(hits - human).toLocaleString()} unverified`
                    : undefined}>
                    {human == null || hits === 0 ? <span className="muted">—</span> : (
                      <>
                        {human.toLocaleString()}
                        <div className="muted" style={{ fontSize: '11px' }}>{Math.round((human / hits) * 100)}%</div>
                      </>
                    )}
                  </td>
                )}
                {show.readRate && <td className="num">{readRate != null ? formatCtr(readRate) : <span className="muted">—</span>}</td>}
                {show.readTime && <td className="num" title={readRate != null ? `${readers.toLocaleString()} of ${metrics.readViews(domain).toLocaleString()} views stayed 30s+` : undefined}>
                  {readTime != null
                    ? <span className={readers < READ_MIN_SAMPLE ? 'muted' : undefined}>{formatDuration(readTime)}</span>
                    : <span className="muted">—</span>}
                </td>}
                {show.adsense && <td className="num">{adsense > 0 ? money(adsense) : <span className="muted">—</span>}</td>}
                {show.amazon && <td className="num"
                    title={hasTag ? `Tracking ID ${domain.amazon_tag}` : `No tracking ID — links use ${FALLBACK_TAG}`}>
                  {amazon > 0 ? money(amazon) : <span className="muted">{hasTag ? '—' : 'no tag'}</span>}
                </td>}
                {show.total && <td className="num" style={{ fontWeight: 600 }}>
                  {total > 0 ? money(total) : <span className="muted" style={{ fontWeight: 400 }}>—</span>}
                </td>}
                {show.impr && <td className="num">{impr > 0 ? impr.toLocaleString() : <span className="muted">—</span>}</td>}
                {show.adsPerHit && <td className="num">{adsPerHit != null ? adsPerHit.toFixed(2) : <span className="muted">—</span>}</td>}
                {show.ecpm && <td className="num">{ecpm != null ? '$' + ecpm.toFixed(2) : <span className="muted">—</span>}</td>}
                {show.rpm && <td className="num">{rpm != null ? '$' + rpm.toFixed(2) : <span className="muted">—</span>}</td>}
                {show.amzImpr && <td className="num">{amzImpr > 0 ? amzImpr.toLocaleString() : <span className="muted">—</span>}</td>}
                {show.amzClicks && <td className="num">
                  {amzImpr > 0 || amzClicks > 0 ? (
                    <>
                      {amzClicks.toLocaleString()}
                      <div className="muted" style={{ fontSize: '11px' }}>{formatCtr(amzImpr ? amzClicks / amzImpr : null)}</div>
                    </>
                  ) : <span className="muted">—</span>}
                </td>}
                {show.trend && <td style={{ textAlign: 'center' }}><Sparkline series={trendSeries} /></td>}
                {show.pages && (
                  <td className="num">
                    {domain.page_count != null ? domain.page_count.toLocaleString() : <span className="muted">—</span>}
                  </td>
                )}
                {show.newMo && (
                  <td className="num">
                    {domain.pages_created_this_month != null ? (
                      <span style={domain.pages_created_this_month > 0 ? { color: '#155724', fontWeight: 600 } : undefined}
                            className={domain.pages_created_this_month > 0 ? undefined : 'muted'}>
                        {domain.pages_created_this_month}
                      </span>
                    ) : <span className="muted">—</span>}
                  </td>
                )}
                {show.scheduled && <td className="num">
                  {domain.pages_scheduled != null ? (
                    <span style={domain.pages_scheduled > 0 ? { color: '#0c5460', fontWeight: 600 } : undefined}
                          className={domain.pages_scheduled > 0 ? undefined : 'muted'}>
                      {domain.pages_scheduled}
                    </span>
                  ) : <span className="muted">—</span>}
                </td>}
                {show.attention && (
                  <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={attentionSummary(domain) || 'Nothing flagged'}>
                    <AttentionBadges domain={domain} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 600, background: 'var(--color-bg)' }}>
            <td className="num small" style={{ textAlign: 'center' }}>{rows.filter(d => d.is_favorite).length}</td>
            <td className="small">Sites total ({rows.length})</td>
            {show.dns && <td />}
            {show.hosting && <td />}
            {show.hits && <td className="num">{totalHits.toLocaleString()}</td>}
            {show.human && (
              <td className="num">
                {humanKnown ? (
                  <>
                    {totalHuman.toLocaleString()}
                    <div className="muted" style={{ fontSize: '11px', fontWeight: 400 }}>
                      {totalHits > 0 ? `${Math.round((totalHuman / totalHits) * 100)}%` : ''}
                    </div>
                  </>
                ) : '—'}
              </td>
            )}
            {show.readRate && <td className="num" title="Readers over tracked views, across the sites shown">
              {sum(metrics.readViews) > 0 ? formatCtr(sum(metrics.readers) / sum(metrics.readViews)) : '—'}
            </td>}
            {show.readTime && <td className="num" title="Median over every reader on every site (medians cannot be added up, so this ignores the filter)">
              {metrics.readTotals ? formatDuration(metrics.readTotals.read_median_s) : '—'}
            </td>}
            {show.adsense && <td className="num">{money(earnings.sites.adsense)}</td>}
            {show.amazon && <td className="num">{money(earnings.sites.amazon)}</td>}
            {show.total && <td className="num">{money(earnings.sites.total)}</td>}
            {show.impr && <td className="num">{totalImpr.toLocaleString()}</td>}
            {show.adsPerHit && <td className="num">{totalHits > 0 ? (totalImpr / totalHits).toFixed(2) : '—'}</td>}
            {show.ecpm && <td className="num" title="Averaged over ad-tracked sites only — sites with AdSense earnings but no Snowplow impressions are excluded">
              {trackedImpr > 0 ? '$' + ((trackedEarn / trackedImpr) * 1000).toFixed(2) : '—'}
            </td>}
            {show.rpm && <td className="num" title="Averaged over sites with page views only — earnings on sites with no hits are excluded">
              {viewedHits > 0 ? '$' + ((viewedEarn / viewedHits) * 1000).toFixed(2) : '—'}
            </td>}
            {show.amzImpr && <td className="num">{sum(metrics.amzImpr).toLocaleString()}</td>}
            {show.amzClicks && <td className="num">
              {sum(metrics.amzClicks).toLocaleString()}
              <div className="muted" style={{ fontSize: '11px', fontWeight: 400 }}>
                {formatCtr(sum(metrics.amzImpr) ? sum(metrics.amzClicks) / sum(metrics.amzImpr) : null)}
              </div>
            </td>}
            {show.trend && <td />}
            {show.pages && <td className="num">{sum(d => d.page_count).toLocaleString()}</td>}
            {show.newMo && <td className="num">{sum(d => d.pages_created_this_month)}</td>}
            {show.scheduled && <td className="num">{sum(d => d.pages_scheduled)}</td>}
            {show.attention && <td className="small">{rows.filter(needsAttention).length} need attention</td>}
          </tr>
          {/* The earnings rows span everything before and after the three
              money columns, so they line up whatever columns are showing. */}
          {(() => {
            // only views that show all three money columns get the earnings rows
            const spans = moneySpans(show);
            if (!spans) return null;
            const { before, after } = spans;
            const row = (key, label, a, z, t, title) => (
              <tr key={key} style={{ background: 'var(--color-bg)' }}>
                <td colSpan={before} className="small" style={{ textAlign: 'right' }} title={title}>{label}</td>
                <td className="num">{money(a)}</td>
                <td className="num">{money(z)}</td>
                <td className="num" style={{ fontWeight: 600 }}>{money(t)}</td>
                {after > 0 && <td colSpan={after} />}
              </tr>
            );
            const out = [];
            if (earnings.unattributed) {
              out.push(row('unattributed', 'Amazon not attributed to a site', null, earnings.unattributed,
                earnings.unattributed,
                `${FALLBACK_TAG} (the fallback every untagged site uses), tracking IDs no site has, and shared IDs`));
            }
            out.push(row('portfolio', <strong>Portfolio total · {periodLabel}{earnings.partial ? ' (partial)' : ''}</strong>,
              earnings.portfolio.adsense, earnings.portfolio.amazon, earnings.portfolio.total,
              earnings.partial
                ? `Missing: ${[!earnings.adsensePeriod.available && earnings.adsensePeriod.note, !earnings.amazonPeriod.available && earnings.amazonPeriod.note].filter(Boolean).join('; ')}`
                : 'All AdSense and all Amazon earnings for the period, over the sites shown plus unattributed Amazon'));
            return out;
          })()}
        </tfoot>
      </table>
    </div>
  );
}
